import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ordersAPI, jobsAPI } from '../services/api';
import { useOAuth } from '../context/OAuthContext';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import CharacterSelection from '../components/oauth/CharacterSelection';
import CharacterInfoEditor from '../components/oauth/CharacterInfoEditor';
import { useAdmin } from '../context/AdminContext';
import Toast from '../components/shared/Toast';
import ApplicationProgress from '../components/shared/ApplicationProgress';
import useApplicationUpdates from '../hooks/useApplicationUpdates';
import { getApplicationProgress } from '../utils/applicationStatusModel';
import './UserAccount.css';
import '../App.css';
import './UserAccount.css';

const loadAccountApplications = options => jobsAPI.getMyApplications(options);

const UserAccount = () => {
  const { currentUser, isInitialized, login, logout, verifyUser } = useOAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('information');
  const [showCharacterSelection, setShowCharacterSelection] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');
  const { textures: adminTextures, products: adminProducts, isInitialized: adminInitialized } = useAdmin();
  const applicationCharacterId = currentUser?.activeCharacterId || currentUser?.characters?.find(character => character.isActive === true || character.isActive === 1)?.id;
  const applicationUpdates = useApplicationUpdates(loadAccountApplications, {
    enabled: Boolean(isInitialized && currentUser && applicationCharacterId && !showCharacterSelection),
    poll: activeTab === 'applications',
    identity: `${currentUser?.id}:${applicationCharacterId}`
  });
  const applications = applicationUpdates.data || [];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isInitialized && currentUser) {
      const activeCharacterExists = currentUser.activeCharacterId !== null &&
                                   currentUser.activeCharacterId !== undefined &&
                                   currentUser.characters &&
                                   currentUser.characters.some(c => c.id === currentUser.activeCharacterId);
      const hasActiveCharacterFlag = currentUser.characters &&
                                    currentUser.characters.some(c => c.isActive === true || c.isActive === 1);
      const hasActiveCharacter = activeCharacterExists || hasActiveCharacterFlag;

      const forceSelection = searchParams.get('forceCharacterSelection') === 'true';
      if ((!hasActiveCharacter || forceSelection) && currentUser.characters && currentUser.characters.length > 0) {
        setShowCharacterSelection(true);
        return;
      }

      if (hasActiveCharacter) {
        fetchUserOrders();
      }
    }
  }, [isInitialized, currentUser, searchParams]);

  const fetchUserOrders = async () => {
    if (!currentUser) {
      console.log('fetchUserOrders: No current user');
      return;
    }

    console.log('fetchUserOrders: Current user ID:', currentUser.id);
    setLoadingOrders(true);
    try {
      const response = await ordersAPI.getMyOrders();
      console.log('fetchUserOrders: Response:', response);
      if (response.success) {
        console.log('fetchUserOrders: Orders found:', response.data?.length || 0);
        setOrders(response.data || []);
      } else {
        console.error('Failed to fetch orders:', response.message);
        setOrders([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleLogin = () => {
    login('/account');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffa500';
      case 'reviewing': return '#4169e1';
      case 'interviewing': return '#9932cc';
      case 'accepted': return '#32cd32';
      case 'rejected': return '#dc143c';
      case 'paid': return '#32cd32';
      case 'processing': return '#4169e1';
      case 'shipped': return '#9932cc';
      case 'delivered': return '#32cd32';
      case 'cancelled': return '#dc143c';
      default: return '#666';
    }
  };

  const getProductName = (productId) => {
    if (!adminInitialized || !adminProducts) return `Product #${productId}`;
    const product = adminProducts.find(p => p.id === productId);
    return product ? product.name : `Product #${productId}`;
  };

  const getProductImage = (productId) => {
    if (!adminInitialized || !adminProducts) return null;
    const product = adminProducts.find(p => p.id === productId);
    if (!product || !product.images) return null;

    const imageKeys = Object.keys(product.images);
    if (imageKeys.length === 0) return null;

    const firstImage = product.images[imageKeys[0]];
    if (Array.isArray(firstImage) && firstImage.length > 0) {
      return firstImage[0];
    }
    return firstImage;
  };

  const getProductPrice = (productId) => {
    if (!adminInitialized || !adminProducts) return 0;
    const product = adminProducts.find(p => p.id === productId);
    return product ? parseFloat(product.price) : 0;
  };

  if (!isInitialized) {
    return (
      <div className="user-account-page-wrapper">
        <ResponsiveNavbar
          menuItems={[
            { type: 'link', label: 'Veloura', to: '/' },
            { type: 'link', label: 'Shop', to: '/shop' },
            { type: 'link', label: 'Team', to: '/team' },
            { type: 'link', label: 'Careers', to: '/careers' },
            { type: 'link', label: 'Partners', to: '/partners' },
            { type: 'link', label: 'News', to: '/news' }
          ]}
          logo="VELOURA"
          logoLink="/"
          scrolled={scrolled}
        />
        <div className="user-account-page">
          <div className="user-account-container">
            <p>Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="user-account-page-wrapper">
        <ResponsiveNavbar
          menuItems={[
            { type: 'link', label: 'Veloura', to: '/' },
            { type: 'link', label: 'Shop', to: '/shop' },
            { type: 'link', label: 'Team', to: '/team' },
            { type: 'link', label: 'Careers', to: '/careers' },
            { type: 'link', label: 'Partners', to: '/partners' },
            { type: 'link', label: 'News', to: '/news' }
          ]}
          logo="VELOURA"
          logoLink="/"
          scrolled={scrolled}
        />
        <div className="user-account-page">
          <div className="user-account-container">
            <h1>My Account</h1>
            <p>Please log in with your GTA World account to view your orders and applications.</p>
            <button onClick={handleLogin} className="oauth-login-button">
              Login with GTA World
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  const activeCharacterExists = currentUser.activeCharacterId !== null &&
                               currentUser.activeCharacterId !== undefined &&
                               currentUser.characters &&
                               currentUser.characters.some(c => c.id === currentUser.activeCharacterId);
  const hasActiveCharacterFlag = currentUser.characters &&
                                currentUser.characters.some(c => c.isActive === true || c.isActive === 1);
  const hasActiveCharacter = activeCharacterExists || hasActiveCharacterFlag;
  const needsCharacterSelection = !hasActiveCharacter && currentUser.characters && currentUser.characters.length > 0;
  const activeCharacter = currentUser.characters?.find(c =>
    (c.isActive === true || c.isActive === 1) || c.id === currentUser.activeCharacterId
  );
  return (
    <div className="user-account-page-wrapper">
      <ResponsiveNavbar
        menuItems={[
          { type: 'link', label: 'Home', to: '/' },
          { type: 'link', label: 'Shop', to: '/shop' },
          { type: 'link', label: 'Team', to: '/team' },
          { type: 'link', label: 'Careers', to: '/careers' },
          { type: 'link', label: 'Partners', to: '/partners' },
          { type: 'link', label: 'News', to: '/news' },
          { type: 'button', label: 'Logout', onClick: handleLogout }
        ]}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />
      <div className="user-account-page">
        <div className="user-account-container">
          <div className="account-header">
            <h1>Welcome, {currentUser.username}!</h1>
            {activeCharacter && (
              <div className="active-character-section">
                <p className="active-character">
                  Active Character: {activeCharacter.firstname} {activeCharacter.lastname}
                </p>
                {currentUser.characters && currentUser.characters.length > 1 && (
                  <button
                    onClick={() => setShowCharacterSelection(true)}
                    className="switch-character-button"
                  >
                    Switch Character
                  </button>
                )}
              </div>
            )}
          </div>
          {showCharacterSelection && currentUser.characters && currentUser.characters.length > 0 && (
            <div className="character-selection-modal" onClick={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
              }
            }}>
              <div className="character-selection-modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Select Your Character</h2>
                <p>You must select a character to continue. Please choose which character you'd like to use for your account.</p>
                <CharacterSelection
                  characters={currentUser.characters || []}
                  onCharacterSelected={async () => {
                    setShowCharacterSelection(false);
                    await verifyUser();
                    window.location.reload();
                  }}
                />
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                    className="close-character-selection-button"
                    style={{ background: '#666' }}
                  >
                    Logout Instead
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="account-tabs">
            <button
              className={`tab-button ${activeTab === 'information' ? 'active' : ''}`}
              onClick={() => setActiveTab('information')}
            >
              My Information
            </button>
            <button
              className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              My Orders ({orders.length})
            </button>
            <button
              className={`tab-button ${activeTab === 'applications' ? 'active' : ''}`}
              onClick={() => setActiveTab('applications')}
            >
              My Applications ({applications.length})
            </button>
          </div>
          {activeTab === 'information' && activeCharacter && (
            <section className="account-section">
              <div className="order-invoice-card">
                <div className="invoice-header">
                  <div className="invoice-logo-section">
                    <div className="invoice-brand">
                      <p className="invoice-subtitle">Character Information</p>
                    </div>
                  </div>
                  <div className="invoice-status-section">
                    <span className="order-status-badge" style={{ backgroundColor: '#32cd32' }}>
                      ACTIVE
                    </span>
                  </div>
                </div>
                <div className="invoice-details-grid">
                  <div className="invoice-detail-item">
                    <span className="invoice-label">Character Name:</span>
                    <span className="invoice-value">{activeCharacter.firstname} {activeCharacter.lastname}</span>
                  </div>
                  <div className="invoice-detail-item">
                    <span className="invoice-label">Character ID:</span>
                    <span className="invoice-value">#{activeCharacter.characterId || activeCharacter.id}</span>
                  </div>
                </div>
                <div className="invoice-items-section">
                  <CharacterInfoEditor
                    character={activeCharacter}
                    onUpdate={async () => {
                      await verifyUser();
                    }}
                  />
                </div>
              </div>
            </section>
          )}
          {activeTab === 'orders' && (
            <section className="account-section">
              {loadingOrders ? (
                <p>Loading orders...</p>
              ) : orders.length === 0 ? (
                <p>You have no orders yet.</p>
              ) : (
                <div className="orders-list">
                  {orders.map(order => (
                    <div key={order.id} className="order-invoice-card">
                      <div className="invoice-header">
                        <div className="invoice-logo-section">
                          <div className="invoice-brand">
                            <p className="invoice-subtitle">Order Invoice</p>
                          </div>
                        </div>
                        <div className="invoice-status-section">
                          <span className="order-status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="invoice-details-grid">
                        <div className="invoice-detail-item">
                          <span className="invoice-label">Order ID:</span>
                          <span className="invoice-value">{order.order_id}</span>
                        </div>
                        <div className="invoice-detail-item">
                          <span className="invoice-label">Date:</span>
                          <span className="invoice-value">{new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        <div className="invoice-detail-item">
                          <span className="invoice-label">Total:</span>
                          <span className="invoice-value invoice-total">${parseFloat(order.total).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="invoice-items-section">
                        <h3 className="invoice-section-title">Items</h3>
                        <div className="invoice-items-list">
                          {order.items.map((item, idx) => {
                            const productImage = getProductImage(item.productId);
                            return (
                              <div key={idx} className="invoice-item-row">
                                {productImage && (
                                  <div className="invoice-item-image">
                                    <img src={productImage} alt={getProductName(item.productId)} />
                                  </div>
                                )}
                                <div className="invoice-item-details">
                                  <div className="invoice-item-name">{getProductName(item.productId)}</div>
                                  <div className="invoice-item-meta">
                                    <span>Quantity: {item.quantity}</span>
                                    {item.texture && <span>Texture: {item.texture}</span>}
                                  </div>
                                </div>
                                <div className="invoice-item-price">
                                  ${((item.price || getProductPrice(item.productId)) * item.quantity).toFixed(2)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="invoice-footer">
                        <div className="invoice-actions">
                          <Link to={`/confirmation/${order.order_id}`} className="invoice-button invoice-button-primary">
                            View Order Details
                          </Link>
                          {['paid', 'shipped', 'delivered'].includes((order.status || '').toLowerCase()) && (
                            <button
                              onClick={async () => {
                                try {
                                  const response = await ordersAPI.regenerateDownload(order.id);
                                  if (response.success) {
                                    await fetchUserOrders();
                                    setToastType('success');
                                    setToastMessage('Download link regenerated successfully.');
                                    setTimeout(() => setToastMessage(null), 3000);
                                  }
                                } catch (error) {
                                  console.error('Error regenerating download:', error);
                                  setToastType('error');
                                  setToastMessage(error.message || 'Failed to regenerate download link.');
                                  setTimeout(() => setToastMessage(null), 3000);
                                }
                              }}
                              className="invoice-button invoice-button-secondary"
                            >
                              Regenerate Download Link
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
          {activeTab === 'applications' && (
            <section className="account-section">
              <div className="account-application-refresh" role="status" aria-live="polite">
                {applicationUpdates.error
                  ? `${applicationUpdates.error}${applicationUpdates.data !== null ? ' Showing the last successfully received status.' : ''}`
                  : applicationUpdates.refreshing && applicationUpdates.data !== null
                    ? 'Checking for application updates...'
                    : applicationUpdates.updatedAt
                      ? `Status checked at ${applicationUpdates.updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : ''}
              </div>
              {applicationUpdates.loading ? (
                <p>Loading applications...</p>
              ) : applicationUpdates.error && applicationUpdates.data === null ? (
                <p>Application status is currently unavailable.</p>
              ) : applications.length === 0 ? (
                <p>You have not submitted any job applications yet.</p>
              ) : (
                <div className="applications-list">
                  {applications.map(app => (
                    <div key={app.id} className="order-invoice-card">
                      <div className="invoice-header">
                        <div className="invoice-logo-section">
                          <div className="invoice-brand">
                            <p className="invoice-subtitle">Job Application</p>
                          </div>
                        </div>
                        <div className="invoice-status-section">
                          <span className="order-status-badge" style={{ backgroundColor: getStatusColor(app.status) }}>
                            {getApplicationProgress(app.status).label}
                          </span>
                        </div>
                      </div>
                      <div className="invoice-details-grid">
                        <div className="invoice-detail-item">
                          <span className="invoice-label">Position:</span>
                          <span className="invoice-value">{app.position_title}</span>
                        </div>
                        <div className="invoice-detail-item">
                          <span className="invoice-label">Applied On:</span>
                          <span className="invoice-value">{new Date(app.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        <div className="invoice-detail-item">
                          <span className="invoice-label">Application ID:</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span className="invoice-value">{app.application_token.substring(0, 16)}...</span>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(app.application_token);
                                  setToastType('success');
                                  setToastMessage('Application ID copied to clipboard!');
                                  setTimeout(() => setToastMessage(null), 3000);
                                } catch (err) {
                                  console.error('Failed to copy:', err);
                                  const textArea = document.createElement('textarea');
                                  textArea.value = app.application_token;
                                  document.body.appendChild(textArea);
                                  textArea.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(textArea);
                                  setToastType('success');
                                  setToastMessage('Application ID copied to clipboard!');
                                  setTimeout(() => setToastMessage(null), 3000);
                                }
                              }}
                              className="copy-button"
                              title="Copy full Application ID"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="invoice-items-section">
                        <h3 className="invoice-section-title">Application Details</h3>
                        <div className="invoice-items-list">
                          <div className="invoice-item-row">
                            <div className="invoice-item-details">
                              <div className="invoice-item-name">{app.first_name} {app.last_name}</div>
                              <div className="invoice-item-meta">
                                <span>Email: {app.email}</span>
                                {app.phone && <span>Phone: {app.phone}</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <ApplicationProgress status={app.status} className="account-application-progress" />
                      <div className="invoice-footer">
                        <div className="invoice-actions">
                          <Link to={`/application/${app.application_token}`} className="invoice-button invoice-button-primary">
                            View Application Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
      <Footer />
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  );
};

export default UserAccount;
